import { fileKindLabel, formatDate, formatFileSize, formatTime, toBerlinDateKey } from '@brimo/shared';
import type { DocumentRow } from '../contacts/types';

/** "heute, 09:24" · "gestern, 16:02" · "12.09.2026" — the time column of the activity and protocol cards. */
export function activityDay(at: string, now: Date = new Date()): string {
  const day = toBerlinDateKey(at);
  const today = toBerlinDateKey(now);
  if (day === today) return `heute, ${formatTime(at)}`;
  const yesterday = new Date(now.getTime() - 86_400_000);
  if (day === toBerlinDateKey(yesterday)) return `gestern, ${formatTime(at)}`;
  return formatDate(at);
}

/** "PDF · 8,4 MB" — the right side of a document row (the API's `meta` also carries the date). */
export function fileMeta(row: Pick<DocumentRow, 'mime' | 'size' | 'meta'>): string {
  const parts: string[] = [];
  if (row.mime) parts.push(fileKindLabel(row.mime));
  if (row.size !== null) parts.push(formatFileSize(row.size));
  return parts.length > 0 ? parts.join(' · ') : row.meta;
}

/** "16.09., 10:12" — history rows of the unit record. */
export function shortDateTime(at: string): string {
  const [day, month] = formatDate(at).split('.');
  return `${day ?? ''}.${month ?? ''}., ${formatTime(at)}`;
}

/** "16.09.2026, 10:12" */
export function dateWithTime(at: string): string {
  return `${formatDate(at)}, ${formatTime(at)}`;
}

/** "29.09." — a day without the year (Objekte 03 "bis 29.09."). */
export function shortDay(at: string): string {
  const [day, month] = formatDate(at).split('.');
  return `${day ?? ''}.${month ?? ''}.`;
}

/** Whole calendar days between two instants (Europe/Berlin), never negative. */
export function daysBetween(from: string, to: string): number {
  const a = new Date(toBerlinDateKey(from)).getTime();
  const b = new Date(toBerlinDateKey(to)).getTime();
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

/** Locale-fixed number with up to `decimals` fraction digits ("1.812,6"). */
export function formatNumber(value: number, decimals = 1): string {
  return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: decimals }).format(
    value,
  );
}

/** "1.812,6" → 1812.6; "" → null; unreadable → null. */
export function parseDecimal(text: string): number | null {
  const compact = text.replace(/[\s\u00a0\u202f€%m²]/g, '');
  if (!compact) return null;
  let normalized: string;
  if (compact.includes(',')) normalized = compact.replaceAll('.', '').replace(',', '.');
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(compact)) normalized = compact.replaceAll('.', '');
  else normalized = compact;
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  return Number(normalized);
}

export function parseInteger(text: string): number | null {
  const value = parseDecimal(text);
  return value === null ? null : Math.round(value);
}
